import React, {useEffect, useState} from 'react'
import {FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import {useRetryCountdown} from '../common/useRetryCountdown'
import { OTPInput } from '../../../common/component/OTPInput'
import { sendCoApplicantInviteOtp, sendWizardOtp } from '../../api'
import { IsInviteApplication } from '../wizard/coApplicantInviteContext'

// VerifyOtp props
interface VerifyOtpProps {
  register: UseFormRegister<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData> // add errors
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
}

const VerifyOtp: React.FC<VerifyOtpProps> = ({errors, watch, setValue}) => {
  const mobileNumber = watch('mobileNumber')
  const otpRetryAfterSeconds = watch('otpRetryAfterSeconds')
  const otpRequiresNewCode = watch('otpRequiresNewCode')
  const renterRole = watch('renterRole')
  const coApplicantInviteId = watch('coApplicantInviteId')
  const isInvite = IsInviteApplication({renterRole})
  const [isResending, setIsResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const {seconds: cooldownSeconds, start: startCooldown} = useRetryCountdown()

  useEffect(() => {
    setValue('otpCode', '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [setValue])

  useEffect(() => {
    if (!otpRetryAfterSeconds || otpRetryAfterSeconds <= 0) {
      return
    }

    startCooldown(otpRetryAfterSeconds)
  }, [otpRetryAfterSeconds, startCooldown])

  useEffect(() => {
    if (!otpRequiresNewCode) {
      return
    }

    setValue('otpCode', '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [otpRequiresNewCode, setValue])

  function formatAustralianMobile(number: string | undefined) {
    if (!number) return ''
    const digits = number.replace(/\D/g, '')

    if (!/^04\d{8}$/.test(digits)) return number

    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
  }

  const handleResend = async () => {
    if (isResending || cooldownSeconds > 0) {
      return
    }

    if (!mobileNumber) {
      setResendMessage('Mobile number is missing.')
      return
    }

    setIsResending(true)
    setResendMessage(null)

    try {
      if (isInvite) {
        if (!coApplicantInviteId?.trim()) {
          setResendMessage('Invite reference is missing. Refresh and try again.')
          return
        }

        const result = await sendCoApplicantInviteOtp({ inviteId: coApplicantInviteId.trim(), mobileNumber })

        if (!result.success) {
          setResendMessage(result.message ?? 'Unable to resend code right now.')
          if (result.retryAfterSeconds && result.retryAfterSeconds > 0) {
            startCooldown(result.retryAfterSeconds)
            setValue('otpRetryAfterSeconds', result.retryAfterSeconds)
          }
          if (result.isLockedOut) {
            const lockedUntilUtc =
              result.lockedUntilUtc ??
              (result.retryAfterSeconds && result.retryAfterSeconds > 0
                ? new Date(Date.now() + result.retryAfterSeconds * 1000).toISOString()
                : undefined)
            setValue('otpLockedUntilUtc', lockedUntilUtc)
          }
          setValue('otpRequiresNewCode', result.requiresNewCode ?? false)
          return
        }

        startCooldown(result.retryAfterSeconds ?? 60)
        setValue('otpRetryAfterSeconds', result.retryAfterSeconds ?? 60)
        setValue('otpLockedUntilUtc', undefined)
        setValue('otpRequiresNewCode', false)
        setResendMessage('A new code has been sent.')
        return
      }

      const result = await sendWizardOtp({
        mobileNumber,
      })

      if (!result.sent) {
        setResendMessage(result.message ?? 'Unable to resend code right now.')
        if (result.retryAfterSeconds && result.retryAfterSeconds > 0) {
          startCooldown(result.retryAfterSeconds)
          setValue('otpRetryAfterSeconds', result.retryAfterSeconds)
        }
        if (result.isLockedOut) {
          const lockedUntilUtc =
            result.lockedUntilUtc ??
            (result.retryAfterSeconds && result.retryAfterSeconds > 0
              ? new Date(Date.now() + result.retryAfterSeconds * 1000).toISOString()
              : undefined)
          setValue('otpLockedUntilUtc', lockedUntilUtc)
        }
        if (result.isLockedOut) {
          setValue('otpRequiresNewCode', true)
        }
        return
      }

      startCooldown(result.retryAfterSeconds ?? 60)
      setValue('otpRetryAfterSeconds', result.retryAfterSeconds ?? 60)
      setValue('otpLockedUntilUtc', undefined)
      setValue('otpRequiresNewCode', false)
      setResendMessage('A new code has been sent.')
    } catch {
      setResendMessage('Unable to resend code right now.')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className='wizard-card wizard-auth-card bg-white rounded-4 shadow-sm p-4 p-md-6'>
      <div className='text-center text-dark wizard-auth-head mb-10'>
        <h2 className='wizard-auth-title wizard-auth-title-otp mt-10'>Check your texts</h2>

        <p className='wizard-auth-subtitle'>
          We sent a 6-digit code to
          <br />
          <strong className='wizard-auth-subtitle-strong'>
            {formatAustralianMobile(mobileNumber)}
          </strong>
        </p>
      </div>
      <div className='text-center text-dark wizard-otp-row'>
        <OTPInput
          name='otpCode'
          length={6}
          numericOnly={true}
          disabled={Boolean(otpRequiresNewCode)}
          required='OTP is required'
          onChange={(val) => {
            if (otpRequiresNewCode) {
              return
            }

            if (val.length >= 6) {
              setValue('otpCode', val, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              })
            }
          }}
          className='wizard-otp-input my-3 justify-content-center'
        />
      </div>
      <div className='text-center mt-10 mb-6'>
        <button
          type='button'
          onClick={handleResend}
          className='btn btn-link wizard-otp-resend'
          disabled={isResending || cooldownSeconds > 0}
        >
          {isResending ? 'Resending...' : 'Resend Code'}
        </button>
        {cooldownSeconds > 0 && <span className='small text-muted ms-2'>({cooldownSeconds}s)</span>}
      </div>
      {resendMessage && <p className='text-center small text-muted mt-2 mb-0'>{resendMessage}</p>}
      {otpRequiresNewCode && (
        <p className='text-center small text-warning mt-2 mb-0'>
          Request a new code before trying to verify again.
        </p>
      )}
    </div>
  )
}

export default VerifyOtp
